"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { useApp } from "@/components/AppContext";
import { useI18n } from "@/i18n/I18nProvider";
import { LanguageSwitcher } from "@/i18n/LanguageSwitcher";
import { localizeError } from "@/i18n/errors";
import { BrandMark } from "@/components/BrandMark";

export default function LoginPage() {
  const router = useRouter();
  const { refreshAuth } = useApp();
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      await res.json().catch(() => null);
      setNotice(t("auth.forgotNotice"));
    } catch {
      setError(t("auth.errors.network"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error ? localizeError(locale, data.error) : t("auth.errors.loginFailed"));
        setLoading(false);
        return;
      }

      await refreshAuth();
      router.push("/");
    } catch (err) {
      console.error("Login request error:", err);
      setError(t("auth.errors.network"));
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-main, #f8f9fa)",
        padding: "24px",
        position: "relative",
      }}
    >
      <div style={{ position: "absolute", top: 16, right: 16 }}>
        <LanguageSwitcher />
      </div>
      <div
        style={{
          width: "100%",
          maxWidth: "400px",
          background: "var(--card-bg, #ffffff)",
          border: "1px solid var(--border-subtle, #e5e7eb)",
          borderRadius: "8px",
          padding: "32px 28px",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "14px" }}>
            <BrandMark size={56} animated />
          </div>
          <h1 style={{ fontSize: "20px", fontWeight: 700, color: "var(--text-primary, #111827)", margin: 0 }}>
            {t("auth.title")}
          </h1>
          <p style={{ fontSize: "13px", color: "var(--text-secondary, #6b7280)", marginTop: "6px", marginBottom: 0 }}>
            {t("auth.subtitle")}
          </p>
        </div>

        {error && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: "6px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: "13px",
              marginBottom: "20px",
              lineHeight: 1.4,
            }}
          >
            {error}
          </div>
        )}

        {notice && (
          <div style={{ padding: "10px 14px", borderRadius: "6px", background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", fontSize: "13px", marginBottom: "20px", lineHeight: 1.4 }}>
            {notice}
          </div>
        )}

        <form onSubmit={forgotMode ? handleForgot : handleSubmit}>
          <div style={{ marginBottom: "16px" }}>
            <label
              htmlFor="email"
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: 600,
                color: "var(--text-primary, #374151)",
                marginBottom: "6px",
              }}
            >
              {t("auth.email")}
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.emailPlaceholder")}
              style={{
                width: "100%",
                height: "36px",
                padding: "0 12px",
                borderRadius: "6px",
                border: "1px solid var(--border-subtle, #d1d5db)",
                fontSize: "13px",
                color: "var(--text-primary, #111827)",
                boxSizing: "border-box",
                outline: "none",
              }}
            />
          </div>

          {!forgotMode && (
          <div style={{ marginBottom: "22px" }}>
            <label
              htmlFor="password"
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: 600,
                color: "var(--text-primary, #374151)",
                marginBottom: "6px",
              }}
            >
              {t("auth.password")}
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              style={{
                width: "100%",
                height: "36px",
                padding: "0 12px",
                borderRadius: "6px",
                border: "1px solid var(--border-subtle, #d1d5db)",
                fontSize: "13px",
                color: "var(--text-primary, #111827)",
                boxSizing: "border-box",
                outline: "none",
              }}
            />
          </div>

          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              height: "38px",
              borderRadius: "6px",
              border: "none",
              background: "#18181b",
              color: "#ffffff",
              fontSize: "13px",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1,
              transition: "background 0.15s ease",
            }}
          >
            {forgotMode ? (loading ? t("auth.sending") : t("auth.sendReset")) : loading ? t("auth.signingIn") : t("auth.signIn")}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: "14px" }}>
          <button
            type="button"
            onClick={() => { setForgotMode(!forgotMode); setError(null); setNotice(null); }}
            style={{ background: "none", border: "none", color: "#4b5563", fontSize: "12px", cursor: "pointer", textDecoration: "underline" }}
          >
            {forgotMode ? t("auth.backToSignIn") : t("auth.forgot")}
          </button>
        </div>

        {process.env.NODE_ENV !== "production" && (
        <div
          style={{
            marginTop: "24px",
            paddingTop: "16px",
            borderTop: "1px solid var(--border-subtle, #f3f4f6)",
            fontSize: "11px",
            color: "var(--text-muted, #9ca3af)",
            textAlign: "center",
          }}
        >
          <span>{t("auth.demoAccounts")}</span>
        </div>
        )}
      </div>
    </div>
  );
}
